import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Alert, Flex, ProgressBar } from '@zillow/constellation';
import {
  Button,
  ButtonGroup,
  Divider,
  Form,
  FormField,
  Heading,
  Image,
  Label,
  Modal,
  Paragraph,
} from '@zillow/constellation-10';
import { isEqual } from '@zg-rentals/zdash';

import { RMWContext } from '../../providers/RMWContext';

import { SingleFamilyEnterpriseSelectors } from '../../selectors';

import AppSelectors from '../../selectors/AppSelectors';
import { useSelector } from '../../selectors/useSelector';

import useContracts from '../../hooks/useContracts';
import useValidateListing from '../../hooks/useValidateListing';

import PostingActions from '../../actions/PostingActions';

import UserActions from '../../actions/UserActions';

import { listingErrors as listingErrorsReducer, listing as listingReducer } from '../../reducers/listing';

import typeCheckUtils from '../../utils/typeCheckUtils';

import AddPropertyConstants from '../../constants/AddPropertyConstants';
import PropertyConstants from '../../constants/PropertyConstants';
import TrackingEventsConstants from '../../constants/TrackingEventsConstants';

import OnboardingUpsellCoffeeTable from '../../images/onboarding_upsell_coffee_table.png';
import NewUserOnboardingCSEvents from '../../model/ClickstreamEvent/NewUserOnboardingCSEvents';

import AddPropertyForm from '../Modal/AddPropertyModal/components/AddPropertyForm';
import ConfirmAddress from '../Modal/AddPropertyModal/components/ConfirmAddress';
import StaticMap from '../StaticMap';

import { useValidateUserProfile } from './hooks';

import PersonalDetails from './components/PersonalDetails';

import PropertyCardPreview from './components/PropertyCardPreview';

import { Left, Right, StyledBackground } from './StyledComponents';

import type { ListingReducerProps } from '../../reducers/listing';

const {
  PROPERTY_TYPES: { COMMUNITY },
  LISTING_TYPES: { RENTAL, ROOM },
  ERROR_CODE: { IS_MULTI_FAMILY_RESTRICTED },
} = PropertyConstants;
const { EMPTY_LISTING, EMPTY_ERROR, NUMBER_OF_UNITS_ERROR, MAX_NUM_UNITS_VALUE, MAX_NUM_UNITS_ERROR } =
  AddPropertyConstants;

type UserProfile = {
  firstName: string;
  lastName: string;
  isPropertyManager: boolean;
  isLandlord: boolean;
  propertyCount: string;
};

export type UserProfileReducerTypes = 'TOGGLE_LANDLORD' | 'TOGGLE_PROPERTY_MANAGER' | keyof UserProfile;

function userProfileReducer(
  state: UserProfile,
  { type, payload }: { type: UserProfileReducerTypes; payload?: string },
) {
  if (type === 'TOGGLE_LANDLORD') {
    return { ...state, isPropertyManager: false, isLandlord: true };
  } else if (type === 'TOGGLE_PROPERTY_MANAGER') {
    return { ...state, isPropertyManager: true, isLandlord: false };
  }
  return { ...state, [type]: payload };
}

const EMPTY_USER_PROFILE = {
  firstName: '',
  lastName: '',
  isPropertyManager: false,
  isLandlord: false,
  propertyCount: '',
};

const STEPS = {
  PERSONAL_DETAILS: 1,
  ADD_PROPERTY: 2,
  CONFIRM_ADDRESS: 3,
};

const WizardUserOnboarding = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.user);
  const { firstName: existingFirstName, lastName: existingLastName } = user;
  const contracts = useSelector((state) => SingleFamilyEnterpriseSelectors.makeContracts(state));
  const isMobile = useSelector((state) => AppSelectors.makeIsMobile(state));
  const shouldShowExtraVisual = useSelector((state) => AppSelectors.makeIsScreenXlGTE(state));
  const isStaticMapAlreadyShowing = shouldShowExtraVisual;
  const { paths } = React.useContext(RMWContext);
  const [step, setStep] = useState(STEPS.PERSONAL_DETAILS);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [listing, setListing] = useReducer(listingReducer, EMPTY_LISTING);
  const [errorObj, setErrorObj] = useReducer(listingErrorsReducer, EMPTY_ERROR);
  const emptyUserProfile = useMemo(
    () => ({ ...EMPTY_USER_PROFILE, firstName: existingFirstName, lastName: existingLastName }),
    [],
  );
  const [userProfile, setUserProfile] = useReducer(userProfileReducer, emptyUserProfile);
  const { userProfileErrors } = useValidateUserProfile(userProfile, setIsSubmitDisabled);
  const [isLoading, setIsLoading] = useState(false);
  const [unitNumberDetected, setUnitNumberDetected] = useState(false);
  const [isValidatedWithoutUnitDetected, setIsValidatedWithoutUnitDetected] = useState(false);
  const [propertyAlreadyPostedWarningMessage, setPropertyAlreadyPostedWarningMessage] = useState<
    string | Array<JSX.Element | string>
  >('');
  const [isRefocusingAddressInput, setIsRefocusingAddressInput] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [isImpreciseLocation, setIsImpreciseLocation] = useState(false);

  const { verifyCanSubmitForm, handleCheckLocationRestriction } = useValidateListing(
    listing,
    setListing,
    errorObj,
    setErrorObj,
    setUnitNumberDetected,
    setIsValidatedWithoutUnitDetected,
    setPropertyAlreadyPostedWarningMessage,
    undefined,
    setIsImpreciseLocation,
  );

  useContracts(setListing, setErrorObj, setIsSubmitDisabled);
  useEffect(() => {
    NewUserOnboardingCSEvents.StartOnboarding({ user });
  }, []);

  const createProperty = async () => {
    const { isRoomForRent, propertyType, address, unit, numUnits, customerId, duplicateAccountListingAlias } = listing;
    const listingType = isRoomForRent === 'true' ? ROOM : RENTAL;
    const isCommunityType = propertyType === COMMUNITY;

    if (duplicateAccountListingAlias) {
      return duplicateAccountListingAlias;
    }
    const responseData = await dispatch(
      PostingActions.property.create({
        listingType,
        propertyType,
        fullAddress: address,
        ...(isCommunityType ? { numUnits } : { unitNumber: unit }),
        customerId: listingType === ROOM ? null : customerId,
      }),
    );

    return responseData;
  };

  const next = useCallback(() => {
    const didUpdateUserProfile = !isEqual(userProfile, emptyUserProfile);
    switch (step) {
      case STEPS.PERSONAL_DETAILS:
        if (didUpdateUserProfile) {
          dispatch(UserActions.saveUserProfile(userProfile))
            // @ts-expect-error
            .then(() => {
              dispatch(UserActions.trackEvent({ trackingEvent: TrackingEventsConstants.COMPLETE_ONBOARDING_FLOW }));
              NewUserOnboardingCSEvents.ProfileBuilderNext({ user });
            })
            .then(() => {
              setStep(STEPS.ADD_PROPERTY);
              if (pageError) {
                setPageError(null);
              }
            })
            .catch(() => {
              setPageError('An error occurred while saving your user profile. Please try again.');
            });
        } else {
          dispatch(UserActions.trackEvent({ trackingEvent: TrackingEventsConstants.COMPLETE_ONBOARDING_FLOW }));
          NewUserOnboardingCSEvents.ProfileBuilderNext({ user });

          setStep(STEPS.ADD_PROPERTY);
        }
        break;
      case STEPS.ADD_PROPERTY:
        setIsSubmitDisabled(true);
        verifyCanSubmitForm({ isPromptUnitNumberCheckRequired: !unitNumberDetected })
          .then((isValid) => {
            if (isValid) {
              NewUserOnboardingCSEvents.AddressEntryNext({ user });
              setStep(STEPS.CONFIRM_ADDRESS);
            }
          })
          .finally(() => {
            setIsSubmitDisabled(false);
          });

        if (isValidatedWithoutUnitDetected) {
          setIsValidatedWithoutUnitDetected(false);
        }

        if (unitNumberDetected && listing.unit) {
          setUnitNumberDetected(false);
        }

        if (propertyAlreadyPostedWarningMessage) {
          setPropertyAlreadyPostedWarningMessage('');
        }

        break;
      case STEPS.CONFIRM_ADDRESS:
        setIsLoading(true);
        setIsSubmitDisabled(true);
        createProperty()
          .then((responseData) => {
            // @ts-expect-error - legacy api does not include types
            const { listingAlias } = responseData;
            // @ts-expect-error - legacy api does not include types
            NewUserOnboardingCSEvents.CreateListing({ user, listing: responseData });
            queryClient.invalidateQueries({ queryKey: ['getProperties'] });
            const { propertyType } = listing;
            const isCommunityType = propertyType === COMMUNITY;
            if (isCommunityType) {
              window.router.transitionTo(`${paths.propertiesPage}/${listingAlias}/listing`);
            } else {
              window.router.transitionTo(`${paths.propertiesPage}/${listingAlias}/dashboard/overview`, {
                showSuccessModal: true,
              });
            }
          })
          .catch((err) => {
            const isMultiFamilyRestricted = err.code === IS_MULTI_FAMILY_RESTRICTED;
            const fieldWithError = isMultiFamilyRestricted ? 'propertyType' : 'address';
            setErrorObj({ type: fieldWithError, payload: err.message });
            setStep(STEPS.ADD_PROPERTY);
          })
          .finally(() => {
            setIsLoading(false);
            setIsSubmitDisabled(false);
          });
        break;
      default:
        break;
    }
  }, [
    step,
    listing,
    errorObj,
    unitNumberDetected,
    isValidatedWithoutUnitDetected,
    propertyAlreadyPostedWarningMessage,
    userProfile,
    pageError,
  ]);

  const back = useCallback(() => {
    switch (step) {
      case STEPS.ADD_PROPERTY:
        NewUserOnboardingCSEvents.AddressEntryGoBack({ user });
        setStep(STEPS.PERSONAL_DETAILS);
        break;
      case STEPS.CONFIRM_ADDRESS:
        NewUserOnboardingCSEvents.AddressVerificationGoBack({ user });
        setIsRefocusingAddressInput(true);
        setStep(STEPS.ADD_PROPERTY);
        break;
      default:
        break;
    }
  }, [step]);

  const close = useCallback(() => {
    switch (step) {
      case STEPS.PERSONAL_DETAILS:
        dispatch(
          UserActions.saveUserProfile({
            ...userProfile,
            firstName: userProfileErrors.firstName ? existingFirstName : userProfile.firstName,
            lastName: userProfileErrors.lastName ? existingLastName : userProfile.lastName,
            propertyCount: userProfileErrors.propertyCount ? 0 : userProfile.propertyCount,
          }),
        )
          // @ts-expect-error
          .then(() => {
            dispatch(UserActions.trackEvent({ trackingEvent: TrackingEventsConstants.COMPLETE_ONBOARDING_FLOW }));
            NewUserOnboardingCSEvents.ProfileBuilderSaveAndExit({ user });
          })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['modules', 'auth-react'] });
          });
        break;
      case STEPS.ADD_PROPERTY:
        NewUserOnboardingCSEvents.AddressEntryExit({ user });
        break;
      case STEPS.CONFIRM_ADDRESS:
        NewUserOnboardingCSEvents.AddressVerificationExit({ user });
        break;
      default:
        break;
    }
    onClose();
  }, [step, userProfile, userProfileErrors]);

  const handleListingChange = (keyName: keyof ListingReducerProps, newValue: string) => {
    const newListing = {
      ...listing,
      [keyName]: newValue,
    };

    setErrorObj({ type: keyName, payload: '' });
    // if the unit was changed, the entire address will be revalidated
    keyName === 'unit' && setErrorObj({ type: 'address', payload: '' });

    if (
      newListing.numUnits &&
      (!typeCheckUtils.isNumeric(newListing.numUnits as string | number | undefined) ||
        String(newListing.numUnits).includes('.'))
    ) {
      setErrorObj({ type: 'numUnits', payload: NUMBER_OF_UNITS_ERROR });
    } else if (
      typeCheckUtils.isNumeric(newListing.numUnits as string | number | undefined) &&
      Number(newListing.numUnits) > MAX_NUM_UNITS_VALUE
    ) {
      setErrorObj({ type: 'numUnits', payload: MAX_NUM_UNITS_ERROR });
    }

    setListing({ type: keyName, payload: keyName === 'numUnits' ? newValue?.trim() : newValue });
  };

  const onChangePropertyType = (newPropertyType: string) => handleListingChange('propertyType', newPropertyType);

  const progressBar = (
    <FormField css={{ maxWidth: '300px' }}>
      <Label size="lg">Step {step} of 3</Label>
      <ProgressBar maxValue={3} value={step} size="md" />
    </FormField>
  );

  const form = (
    <>
      {pageError && <Alert appearance="error" body={pageError} />}
      {step === STEPS.PERSONAL_DETAILS && (
        <PersonalDetails userProfile={userProfile} setUserProfile={setUserProfile} errors={userProfileErrors} />
      )}
      {step === STEPS.ADD_PROPERTY && (
        <>
          <Heading level={4} css={{ marginTop: { base: 'md', lg: 'xl' } }}>
            Now, let&apos;s add your property
          </Heading>
          <Paragraph css={{ marginTop: '400' }}>
            This basic information sets up the property in Zillow Rental Manager. Then you can create a rental listing
            for it.
          </Paragraph>
          <AddPropertyForm
            listing={listing}
            contracts={contracts}
            errorObj={errorObj}
            isSubmitDisabled={isSubmitDisabled}
            setIsSubmitDisabled={setIsSubmitDisabled}
            unitNumberDetected={unitNumberDetected}
            isValidatedWithoutUnitDetected={isValidatedWithoutUnitDetected}
            propertyAlreadyPostedWarningMessage={propertyAlreadyPostedWarningMessage}
            onListingChange={handleListingChange}
            onCheckLocationRestriction={(keyName, newValue) =>
              handleCheckLocationRestriction(onChangePropertyType, keyName, newValue)
            }
            isRefocusingAddressInput={isRefocusingAddressInput}
            setIsRefocusingAddressInput={setIsRefocusingAddressInput}
          />
        </>
      )}
      {step === STEPS.CONFIRM_ADDRESS && (
        <>
          <Heading level={4} css={{ marginTop: { base: 'md', lg: 'xl' }, paddingBottom: { base: 'md', lg: 'lg' } }}>
            Is this information complete and correct?
          </Heading>

          <ConfirmAddress
            address={listing.address}
            isRoomForRent={
              typeof listing.isRoomForRent === 'boolean' ? listing.isRoomForRent : listing.isRoomForRent === 'true'
            }
            numUnits={listing.numUnits}
            propertyType={listing.propertyType}
            unit={listing.unit}
            shouldShowStaticMap={!isStaticMapAlreadyShowing}
            showImpreciseLocationAlert={isImpreciseLocation}
          />
        </>
      )}
    </>
  );

  const supplementaryVisual = (
    <>
      {step === STEPS.PERSONAL_DETAILS && (
        <StyledBackground>
          <Image
            src={OnboardingUpsellCoffeeTable}
            alt="Create and sign leases, screen and message applicants, list your property for rent, and receive rent payments."
            css={{ width: 'max(80%, 500px)', height: 'auto', margin: '600' }}
          />
        </StyledBackground>
      )}
      {step === STEPS.ADD_PROPERTY && <PropertyCardPreview address={listing.address} unit={listing.unit} />}
      {step === STEPS.CONFIRM_ADDRESS && (
        <StyledBackground>
          <StaticMap address={listing.address} size="434x622" showImpreciseLocationAlert={isImpreciseLocation} />
        </StyledBackground>
      )}
    </>
  );

  const closeButtonDataEventId = useMemo(() => {
    if (step === STEPS.PERSONAL_DETAILS) {
      return NewUserOnboardingCSEvents.ProfileBuilderSaveAndExit.data_event_id;
    }
    if (step === STEPS.ADD_PROPERTY) {
      return NewUserOnboardingCSEvents.AddressEntryExit.data_event_id;
    }
    return NewUserOnboardingCSEvents.AddressVerificationExit.data_event_id;
  }, [step]);

  const primaryButtonDataEventId = useMemo(() => {
    if (step === STEPS.PERSONAL_DETAILS) {
      return NewUserOnboardingCSEvents.ProfileBuilderNext.data_event_id;
    }
    if (step === STEPS.ADD_PROPERTY) {
      return NewUserOnboardingCSEvents.AddressEntryNext.data_event_id;
    }
    if (step === STEPS.CONFIRM_ADDRESS) {
      return NewUserOnboardingCSEvents.CreateListing.data_event_id;
    }
    return undefined;
  }, [step]);

  const backButtonDataEventId = useMemo(() => {
    if (step === STEPS.ADD_PROPERTY) {
      return NewUserOnboardingCSEvents.AddressEntryGoBack.data_event_id;
    }
    return NewUserOnboardingCSEvents.AddressVerificationGoBack.data_event_id;
  }, [step]);

  return (
    <Modal open={true} onOpenChange={(open) => !open && close()} size="fullScreen" scrollDocument={true}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Heading
                level={1}
                data-eventid={NewUserOnboardingCSEvents.StartOnboarding.data_event_id}
                data-eventdescription="View modal"
              >
                Zillow Rental Manager
              </Heading>
              <Modal.Close data-testid="WizardUserOnboarding-Exit" data-eventid={closeButtonDataEventId}>
                <Button tone="brand" emphasis="outlined" size="md">
                  {step === STEPS.PERSONAL_DETAILS ? 'Save & exit' : 'Exit'}
                </Button>
              </Modal.Close>
            </Modal.Header>
            <Divider />
            <Modal.Body css={{ padding: '0 !important', display: 'flex' }}>
              {shouldShowExtraVisual ? (
                <Flex display="flex" justifyContent="space-between" flexGrow={1}>
                  <Left flexGrow={1} alignSelf="center">
                    <Form asChild={true} css={{ maxWidth: '532px', margin: 'auto', padding: '400' }}>
                      <div>
                        {progressBar}
                        {form}
                      </div>
                    </Form>
                  </Left>
                  <Right flexGrow={1}>{supplementaryVisual}</Right>
                </Flex>
              ) : (
                <Form asChild={true} css={{ width: '100%', padding: '400' }}>
                  <div>
                    {progressBar}
                    <Divider css={{ marginLeft: '-400', marginRight: '-400' }} />
                    {form}
                  </div>
                </Form>
              )}
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ButtonGroup
                aria-label="Form actions"
                css={{
                  width: '100%',
                  justifyContent: 'space-between',
                  flexDirection: 'row-reverse',
                }}
              >
                <Button
                  tone="brand"
                  emphasis="filled"
                  onClick={next}
                  disabled={isSubmitDisabled}
                  loading={isLoading}
                  css={{ minWidth: '164px' }}
                  data-eventid={primaryButtonDataEventId}
                >
                  {step === STEPS.CONFIRM_ADDRESS ? 'Confirm' : 'Next'}
                </Button>
                {step !== STEPS.PERSONAL_DETAILS && (
                  <Button
                    tone="brand"
                    emphasis="outlined"
                    onClick={back}
                    css={{ minWidth: '164px' }}
                    data-eventid={backButtonDataEventId}
                  >
                    {step === STEPS.CONFIRM_ADDRESS ? (isMobile ? 'Edit' : 'Edit property information') : 'Back'}
                  </Button>
                )}
              </ButtonGroup>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal>
  );
};

export default WizardUserOnboarding;
